import { useState } from 'react'
import type { ComponentProps, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import type { Profile } from 'models/ota/ProfileModel'
import { thinScrollbarCss } from 'styles/globalCss'
import { theme } from 'styles/theme'
import { GhostButton, PrimaryButton, SecondaryButton } from '../../Button/Button'
import { SvgIcons } from '../../Icon/Icon'
import { Spinner } from '../../Spinner/Spinner'
import ErrorScreen from 'pages/ErrorPage'
import { InstalledBadge, LatestBadge, MixedProfileCard, ProfileCard, UnknownProfileCard } from './ProfileCard'

// Number of available versions shown before pressing "View more"
const COLLAPSED_VERSIONS_COUNT = 4

const PopupContainer = styled.div`
    inline-size: 100%;
    text-align: start;
`

const Header = styled.header`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1em;
    padding: 1.25em 1.5em;
    border-block-end: 1px solid ${theme.colors.MediumGrey};
`

const Title = styled.h2`
    margin: 0;
    font-size: 1.15em;
    font-weight: ${theme.fontWeights.bold};
`

const SubTitle = styled.div`
    margin-block-start: 2px;
    color: ${theme.colors.DarkGrey};
    font-size: ${theme.fontSizes.small};
`

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    padding: 1.25em 1.5em;
`

const SectionTitle = styled.div`
    color: ${theme.colors.DarkGrey};
    font-size: ${theme.fontSizes.small};
    font-weight: ${theme.fontWeights.bold};
    text-transform: uppercase;
    &:not(:first-child) {
        margin-block-start: 0.75em;
    }
`

const VersionsList = styled.div<{ $isScrollable: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 0.6em;
    ${({ $isScrollable }) => $isScrollable && css`
        max-block-size: 22em;
        overflow-y: auto;
        padding-inline-end: 0.5em;
        ${thinScrollbarCss}
    `}
`

// An underlined text button. GhostButton fits icon buttons only, since it hides its text.
const TextButton = styled.button.attrs({ type: 'button' })`
    align-self: flex-start;
    padding: 0;
    border: none;
    background: none;
    font-size: ${theme.fontSizes.small};
    text-decoration: underline;
    cursor: pointer;
`

const Footer = styled.footer`
    display: flex;
    justify-content: flex-end;
    gap: 1em;
    padding: 1em 1.5em;
    border-block-start: 1px solid ${theme.colors.MediumGrey};
`

const getAvailableVersionsTitle = (count: number) =>
    count === 0 ? 'No other available versions' :
    count === 1 ? '1 available version' :
    `${count} available versions`

export interface ProfileUpdatePopupProps {
    // "Vehicle <number>", or the number of selected vehicles
    subTitle: string
    // Shown instead of the profiles when loading them failed
    profilesError: ComponentProps<typeof ErrorScreen>['error'] | undefined
    onRetryProfiles: () => Promise<void>
    // undefined when the installed profile is not known, or the vehicles have different installed profiles
    installedProfile: Pick<Profile, 'id' | 'title'> | undefined
    hasMixedInstalledProfiles: boolean
    // Newest release first
    availableProfiles: ReadonlyArray<Pick<Profile, 'id' | 'title'>>
    latestProfileId: string | null
    // The module versions of a profile, shown when its card is open
    renderModuleVersions: (profileId: string) => ReactNode
    isRequesting: boolean
    onUpdate: (profileId: string) => void
    onClose: VoidFunction
}

// The popup for choosing a profile to update vehicles to. Only draws, see ProfileUpdatePopupContainer for the data.
export const ProfileUpdatePopup = ({
    subTitle,
    profilesError,
    onRetryProfiles,
    installedProfile,
    hasMixedInstalledProfiles,
    availableProfiles,
    latestProfileId,
    renderModuleVersions,
    isRequesting,
    onUpdate,
    onClose,
}: ProfileUpdatePopupProps) => {
    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null)
    const [isShowingAllVersions, setIsShowingAllVersions] = useState(false)
    // Id of the profile whose module versions are open. Only one is open at a time.
    const [openProfileId, setOpenProfileId] = useState<string | null>(null)

    const shownProfiles = isShowingAllVersions ? availableProfiles : availableProfiles.slice(0, COLLAPSED_VERSIONS_COUNT)

    const toggleOpenProfile = (profileId: string) => () => {
        setOpenProfileId(openProfileId === profileId ? null : profileId)
    }

    return (
        <Spinner {...{ isSpinning: isRequesting, spinnerTitle: 'Requesting profile update...', size: '4em' }}>
            <PopupContainer>
                <Header>
                    <div>
                        <Title>Check for profile update</Title>
                        <SubTitle>{ subTitle }</SubTitle>
                    </div>
                    <GhostButton {...{ text: 'Close', onClick: onClose }}>
                        <SvgIcons.CancelCrossSvg />
                    </GhostButton>
                </Header>
                <Body>
                    { profilesError !== undefined ?
                        <ErrorScreen {...{ error: profilesError, errorMessagePrefix: 'Error loading profiles', onRetry: onRetryProfiles }} /> :
                        <>
                        <SectionTitle>Current profile</SectionTitle>
                        { installedProfile !== undefined ?
                            <ProfileCard {...{
                                profile: installedProfile,
                                badge: <InstalledBadge />,
                                moduleVersions: renderModuleVersions(installedProfile.id),
                                isOpen: openProfileId === installedProfile.id,
                                onToggle: toggleOpenProfile(installedProfile.id),
                            }} /> :
                            hasMixedInstalledProfiles ? <MixedProfileCard /> : <UnknownProfileCard />
                        }
                        <SectionTitle>{ getAvailableVersionsTitle(availableProfiles.length) }</SectionTitle>
                        { availableProfiles.length > 0 &&
                            <VersionsList {...{ $isScrollable: isShowingAllVersions }}>
                                { shownProfiles.map(profile => <ProfileCard {...{
                                    key: profile.id,
                                    profile,
                                    badge: profile.id === latestProfileId ? <LatestBadge /> : null,
                                    radio: {
                                        name: 'profile-update-version',
                                        isChecked: selectedProfileId === profile.id,
                                        onChecked: () => { setSelectedProfileId(profile.id) },
                                    },
                                    moduleVersions: renderModuleVersions(profile.id),
                                    isOpen: openProfileId === profile.id,
                                    onToggle: toggleOpenProfile(profile.id),
                                }} />) }
                            </VersionsList>
                        }
                        { availableProfiles.length > COLLAPSED_VERSIONS_COUNT &&
                            <TextButton {...{ onClick: () => {
                                setIsShowingAllVersions(!isShowingAllVersions)
                                setOpenProfileId(null)
                            } }}>
                                { isShowingAllVersions ? 'View less' : 'View more' }
                            </TextButton>
                        }
                        </>
                    }
                </Body>
                <Footer>
                    <SecondaryButton {...{ text: 'Cancel', onClick: onClose }} />
                    <PrimaryButton {...{
                        text: 'Update',
                        isDisabled: selectedProfileId === null || isRequesting,
                        onClick: () => {
                            if (selectedProfileId !== null) {
                                onUpdate(selectedProfileId)
                            }
                        },
                    }} />
                </Footer>
            </PopupContainer>
        </Spinner>
    )
}
